import Component from '@glimmer/component'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { t, type IntlService } from 'ember-intl'
import type { ReportData } from 'teacher-ui-client/reporting/ui-scope/maths/skills-mastered/cohort/students-results/route'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import StudentsResultsLegend from 'teacher-ui-client/components/tables/skills-mastered/students-results/legend'
import StudentsResultsGradeTabs from 'teacher-ui-client/components/tables/skills-mastered/students-results/grade-tabs'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import FidgetSpinnerWave from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { LinkTo } from '@ember/routing'
import sticky from 'teacher-ui-client/modifiers/sticky'
import { array } from '@ember/helper'
import { eq, or } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import optional from 'teacher-ui-client/helpers/optional'
import { concat } from '@ember/helper'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { get } from '@ember/helper'
import type { PhosphorIconName } from 'phosphor-icons'

export enum LessonResultCategory {
  TestedAbove = 'tested_above',
  ImmediatePass = 'immediate_pass',
  EventualPass = 'eventual_pass',
  NotPassed = 'not_passed',
}

interface StudentViewModel {
  id: string
  firstName: string
  lastName: string
  highestLessonPassed: number | null
  results: ReportData['students'][number]['results']
}

export interface LessonItem {
  lessonPosition: number
  lessonName: string
  gradePosition: number
}

interface DisplayData {
  column: string
  icon: PhosphorIconName
  ariaDirection: string
}

interface LegendItem {
  categoryClassName: string
  label: string
}

interface Args {
  studentsData?: ReportData['students']
  isLoading?: boolean
  lessonItemsByGrade?: Record<PropertyKey, LessonItem[]>
  sortingConfig?: SortingConfig
  gradePosition?: number
  themeKey?: ThemeKeys
  studentRoute: string
  setGradePosition?: (gradePostion: number) => void
  updateSort?: (sortKey: string) => void
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const themeClasses: Partial<Record<ThemeKeys, { headerBg: string; rowBg: string }>> = {
  [ThemeKeys.blue]: {
    headerBg: 'bg-oceany-blue-100',
    rowBg: 'even:bg-oceany-blue-50 hover:bg-oceany-blue-100',
  },
  [ThemeKeys.green]: {
    headerBg: 'bg-ms-green-100',
    rowBg: 'even:bg-ms-green-50 hover:bg-ms-green-100',
  },
  [ThemeKeys.default]: {
    headerBg: 'bg-dusty-black-100',
    rowBg: 'even:bg-dusty-black-50 hover:bg-dusty-black-100',
  },
}

/**
 * Allows the UI to show a grey box when the child has placed above (due to placement test) or was manually moved via
 * manage progress changes.
 */
function backfillTestedAbove(results: StudentViewModel['results']): StudentViewModel['results'] {
  const highestLessonAttempted = isEmpty(results) ? 0 : Math.max(...Object.keys(results).map(Number))
  const newResults = { ...results }
  const testedAboveResult = { lesson_results: [], lesson_results_category: LessonResultCategory.TestedAbove }

  for (let lessonNumber = 1; lessonNumber < highestLessonAttempted; lessonNumber++) {
    newResults[lessonNumber] ??= testedAboveResult
  }

  return newResults
}

export class SkillsMasteredStudentsResults extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Count of First/last and lesson completed columns on the left side of the table, before results.
   */
  readonly studentColumnCount = 3

  get themeClasses() {
    return themeClasses[this.args.themeKey ?? ThemeKeys.default]
  }

  get studentViewModels(): StudentViewModel[] {
    return (this.args.studentsData ?? []).map<StudentViewModel>((studentData) => {
      return {
        id: studentData.id,
        firstName: studentData.first_name,
        lastName: studentData.last_name,
        highestLessonPassed: studentData.highest_lesson_passed,
        results: backfillTestedAbove(studentData.results),
      }
    })
  }

  /**
   * An array of lesson items to show in the table based on the selected grade position.
   */
  get currentLessons(): LessonItem[] {
    if (typeof this.args.gradePosition === 'number') {
      return this.args.lessonItemsByGrade?.[this.args.gradePosition] ?? []
    }
    return []
  }

  get displaySort(): DisplayData | Record<string, never> {
    const firstSortColumn = this.args.sortingConfig?.columns[0]
    if (firstSortColumn) {
      return {
        column: firstSortColumn.key,
        icon: firstSortColumn.sortDirection === 'asc' ? 'phosphor-caret-up-fill' : 'phosphor-caret-down-fill',
        ariaDirection: firstSortColumn.sortDirection === 'asc' ? 'ascending' : 'descending',
      }
    }
    return {}
  }

  get legendItems(): Record<LessonResultCategory, LegendItem> {
    return {
      tested_above: {
        label: this.intl.t('components.reporting.tables.skillsMastered.studentsResults.legend.testedAbove'),
        categoryClassName: 'bg-dusty-black-100',
      },
      immediate_pass: {
        label: this.intl.t('components.reporting.tables.skillsMastered.studentsResults.legend.immediatePass'),
        categoryClassName: 'bg-forest-green-300',
      },
      eventual_pass: {
        label: this.intl.t('components.reporting.tables.skillsMastered.studentsResults.legend.eventualPass'),
        categoryClassName: 'bg-forest-green-150',
      },
      not_passed: {
        label: this.intl.t('components.reporting.tables.skillsMastered.studentsResults.legend.notPassed'),
        categoryClassName: 'bg-watermelony-red-250',
      },
    }
  }

  <template>
    {{! transform is used to help position some elements in this component that need to be fixed to the scroll container }}
    {{! Specifically the loading spinner }}
    <div class="transform overflow-x-auto" ...attributes>
      {{! Border separate is required if you want sticky cells to keep their borders (we do) }}
      <table class="relative border-separate border-spacing-0">
        <thead>
          {{! Row for title and legend }}
          <tr>
            <th {{sticky column=true}} colspan={{this.studentColumnCount}} class="p-3">
              {{t "components.reporting.tables.skillsMastered.studentsResults.title"}}
            </th>
            <th {{sticky column=true}} class="max-w-0 px-0 py-3 font-normal whitespace-nowrap">
              <StudentsResultsLegend @legendItems={{this.legendItems}} />
            </th>
          </tr>
          {{! Row for the lesson filter tabs }}
          <tr>
            <th {{sticky column=true}} colspan={{this.studentColumnCount}}></th>
            {{! Use the sticky zero-width column to correctly position some horizontally-sticky lesson filter tabs }}
            <th {{sticky column=true}} class="max-w-0 p-0 whitespace-nowrap">
              <StudentsResultsGradeTabs
                @themeKey={{@themeKey}}
                @lessonItemsByGrade={{@lessonItemsByGrade}}
                @gradePosition={{@gradePosition}}
                @setGradePosition={{@setGradePosition}}
              />
            </th>
          </tr>
          {{! Spacer row, room for the rotated text  }}
          <tr>
            <th {{sticky column=true z=1}} colspan={{this.studentColumnCount}} class="h-24 bg-white"></th>
            <th {{sticky column=true}} class="p-0"></th>
            <th colspan={{this.currentLessons.length}} class={{this.themeClasses.headerBg}}></th>
            {{! Extra column to make room for the rotated text }}
            <th class="min-w-32 {{this.themeClasses.headerBg}}"></th>
          </tr>
          {{! Student & lesson header row }}
          <tr class={{this.themeClasses.headerBg}}>
            {{! Student headers }}
            {{#each (array "first_name" "last_name" "highest_lesson_passed") as |columnKey|}}
              <th
                data-test-cell={{columnKey}}
                class="max-w-40 min-w-40 border-r border-b border-white bg-inherit"
                aria-sort={{if (eq this.displaySort.column columnKey) this.displaySort.ariaDirection}}
                {{sticky column=true z=1}}
              >
                <button
                  class="flex w-full items-center justify-between p-3 text-left"
                  type="button"
                  {{on "click" (fn (optional @updateSort) columnKey)}}
                >
                  {{t (concat "components.reporting.tables.skillsMastered.studentsResults.headers." columnKey)}}
                  <PhosphorIcon
                    @icon={{if
                      (eq this.displaySort.column columnKey)
                      this.displaySort.icon
                      "phosphor-caret-up-down-fill"
                    }}
                  />
                </button>
              </th>
            {{/each}}
            {{! Sticky zero-width column }}
            <th {{sticky column=true}} class="p-0"></th>
            {{! Lesson headers }}
            {{#each this.currentLessons as |lessonItem|}}
              <th class="relative align-bottom font-normal" data-test-lesson-column={{lessonItem.lessonPosition}}>
                <div class="absolute top-8 w-48 origin-top-left -rotate-45 truncate text-sm whitespace-nowrap">
                  {{lessonItem.lessonName}}
                </div>
                <div class="w-8 bg-white text-center">{{lessonItem.lessonPosition}}</div>
              </th>
            {{/each}}
            {{! Extra column to make room for the rotated text }}
            <th class="min-w-32 {{this.themeClasses.headerBg}}"></th>
          </tr>
        </thead>
        {{#if @isLoading}}
          <tbody>
            <tr>
              {{! Fixed (rather than absolute) so that it is positioned relative to the scroll container }}
              <td class="fixed left-1/2 -translate-x-1/2 transform p-3 text-center">
                <FidgetSpinnerWave data-test-table-loading @small={{true}} @centered={{true}} />
              </td>
            </tr>
          </tbody>
        {{/if}}
        <tbody class={{if @isLoading "opacity-50"}}>
          {{#each this.studentViewModels as |studentViewModel index|}}
            <tr class="bg-white {{this.themeClasses.rowBg}}" data-test-student-row-index={{index}}>
              {{! Student data }}
              {{! Must have a fixed width for sticky columns, so that new pages with different name lengths dont change the
          cell sizes, which would require repositioning of sticky header cells }}
              <td
                data-test-cell="first_name"
                class="w-px max-w-40 min-w-40 truncate border-r border-white bg-inherit px-3 py-1"
                {{sticky column=true}}
              >
                <LinkTo class="font-bold" @route={{@studentRoute}} @models={{array studentViewModel.id}}>
                  {{studentViewModel.firstName}}
                </LinkTo>
              </td>
              <td
                data-test-cell="last_name"
                class="w-px max-w-40 min-w-40 truncate border-r border-white bg-inherit px-3 py-1"
                {{sticky column=true}}
              >
                <LinkTo class="font-bold" @route={{@studentRoute}} @models={{array studentViewModel.id}}>
                  {{studentViewModel.lastName}}
                </LinkTo>
              </td>
              <td
                data-test-cell="highest_lesson_passed"
                class="border-r border-white bg-inherit px-3 py-1"
                {{sticky column=true}}
              >
                {{formatValue studentViewModel.highestLessonPassed}}
              </td>
              {{! Sticky zero-width column }}
              <td {{sticky column=true}} class="p-0"></td>
              {{! Lesson results }}
              {{#each this.currentLessons as |lessonItem|}}
                {{#let (get studentViewModel.results lessonItem.lessonPosition) as |lessonResults|}}
                  {{#let (get this.legendItems lessonResults.lesson_results_category) as |legendCategory|}}
                    <td
                      data-test-lesson-column={{lessonItem.lessonPosition}}
                      data-test-lesson-result={{lessonResults.lesson_results_category}}
                      class="border-y border-r border-white p-0"
                    >
                      <div class="h-8 {{or legendCategory.categoryClassName 'border-dusty-black-50 border bg-white'}}">
                      </div>
                    </td>
                  {{/let}}
                {{/let}}
              {{/each}}
            </tr>
          {{else}}
            <tr>
              <td colspan={{this.studentColumnCount}} class="p-3 text-center italic" data-test-table-empty>
                {{t "components.reporting.tables.skillsMastered.studentsResults.emptyMessage"}}
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>
    </div>
  </template>
}

export default SkillsMasteredStudentsResults
